import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import { CartProvider } from './lib/cartContext';

// Lazy load ALL non-critical components to reduce initial JS bundle
const Toaster = React.lazy(() => import('@/components/ui/toaster').then(m => ({ default: m.Toaster })));
const ChatBot = React.lazy(() => import('./components/ChatBot'));

// Lazy load pages for better performance
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const CompanyInfoPage = React.lazy(() => import('./pages/CompanyInfoPage'));

// Instant no-layout-shift skeleton fallback
const PageSkeleton = () => (
  <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }} aria-hidden="true" />
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h1>Oops! Something went wrong.</h1>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AppContent = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <div className="app">
      <Navigation />
      <main className="main-content" style={{
        paddingTop: isHome ? '0' : undefined
      }}>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/company-info" element={<CompanyInfoPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      {/* ChatBot and Toaster in separate Suspense — load independently, never block page render */}
      <Suspense fallback={null}>
        <Toaster />
        <ChatBot />
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </Router>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
